import axios from 'axios';
import CryptoJS from 'crypto-js';
import {initialState} from '../states/reducer';

/**
 * iFEx API Service
 * Provides API methods for interacting with the iFEx backend
 * Base URL configured in .env as REACT_APP_IFEX_API_URL
 */

// Create separate API client for iFEx backend (port 8002)
const iFExApi = axios.create({
    baseURL: process.env.REACT_APP_IFEX_API_URL || 'http://localhost:8002/api',
    withCredentials: true // Enable sending cookies with cross-origin requests for Sanctum
});

// Add token interceptor for authentication
iFExApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('ifex_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Helper function to get iFEx API instance
const Api = () => iFExApi;

/**
 * Initialize iFEx authentication by auto-logging in with main app user
 * Call this before making any iFEx API requests
 */
export const initializeiFExAuth = async () => {
    try {
        // Check if already has valid iFEx token
        const existingToken = localStorage.getItem('ifex_token');
        if (existingToken) {
            // Token exists, verify it's still valid
            try {
                await iFExApi.get('/user');
                return true; // Token is valid
            } catch (error) {
                // Token is invalid, remove it and continue to auto-login
                localStorage.removeItem('ifex_token');
            }
        }

        // Get main app user info from encrypted localStorage
        const encryptedUser = localStorage.getItem('user');
        if (!encryptedUser) {
            console.error('No main app user found');
            return false;
        }

        // Decrypt user info
        const bytes = CryptoJS.AES.decrypt(encryptedUser, initialState.secretKey);
        const user = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

        // Call auto-login endpoint
        const response = await axios.post(`${process.env.REACT_APP_IFEX_API_URL || 'http://localhost:8002/api'}/auto-login`, {
            email: user.email || `user${user.ID}@ifex.local`,
            name: user.name || user.vollname || `User ${user.ID}`,
            is_super_admin: user.isSAdmin === '1' || user.role === 'Super Admin'
        }, {
            withCredentials: true // Enable cookies for auto-login
        });

        // Store iFEx token separately
        localStorage.setItem('ifex_token', response.data.token);

        return true;
    } catch (error) {
        console.error('iFEx auto-login failed:', error);
        return false;
    }
};

/**
 * Placeholder Management API
 */
export const placeholderAPI = {
    /**
     * Get all placeholders with optional filtering
     * @param {Object} params - Query parameters (is_active, placeholder_type, search, prefix, sort_field, sort_direction, per_page)
     * @returns {Promise} API response with placeholder list
     */
    getAll: (params) => Api().get('/placeholders', { params }),

    /**
     * Get a single placeholder by ID
     * @param {number} id - Placeholder ID
     * @returns {Promise} API response with placeholder details
     */
    getById: (id) => Api().get(`/placeholders/${id}`),

    /**
     * Create a new placeholder (Super-Admin only)
     * @param {Object} data - Placeholder data (placeholder_name, is_active, database_connection, table_name, field_name, field_format, placeholder_type, text_chain_formula, remarks)
     * @returns {Promise} API response with created placeholder
     */
    create: (data) => Api().post('/placeholders', data),

    /**
     * Update an existing placeholder
     * @param {number} id - Placeholder ID
     * @param {Object} data - Updated placeholder data
     * @returns {Promise} API response
     */
    update: (id, data) => Api().put(`/placeholders/${id}`, data),

    /**
     * Delete a placeholder (Super-Admin only)
     * @param {number} id - Placeholder ID
     * @returns {Promise} API response
     */
    delete: (id) => Api().delete(`/placeholders/${id}`),

    /**
     * Validate placeholder name availability
     * @param {string} name - Placeholder name to validate (without brackets)
     * @returns {Promise} API response with validation result
     */
    validateName: (name) => Api().get(`/placeholders/validate/${name}`),

    /**
     * Test database connection
     * @param {Object} data - Connection data (server, database, username, password)
     * @returns {Promise} API response with connection test result
     */
    testConnection: (data) => Api().post('/placeholders/test-connection', data),
};

/**
 * Document Management API
 */
export const documentAPI = {
    /**
     * Get all documents with optional filtering
     * @param {Object} params - Query parameters (is_active, module, search, sequence, sort_field, sort_direction, per_page)
     * @returns {Promise} API response with document list
     */
    getAll: (params) => Api().get('/documents', { params }),

    /**
     * Get a single document by ID
     * @param {number} id - Document ID
     * @returns {Promise} API response with document details
     */
    getById: (id) => Api().get(`/documents/${id}`),

    /**
     * Upload a new document template (Super-Admin only)
     * @param {FormData} formData - Form data with file and metadata (document_name, sequence, is_active, module, file, remarks)
     * @returns {Promise} API response with created document
     */
    upload: (formData) => Api().post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),

    /**
     * Update document metadata
     * @param {number} id - Document ID
     * @param {Object} data - Updated document data
     * @returns {Promise} API response
     */
    update: (id, data) => Api().put(`/documents/${id}`, data),

    /**
     * Delete a document (Super-Admin only)
     * @param {number} id - Document ID
     * @returns {Promise} API response
     */
    delete: (id) => Api().delete(`/documents/${id}`),

    /**
     * Download a document template
     * @param {number} id - Document ID
     * @returns {Promise} API response with file blob
     */
    download: (id) => Api().get(`/documents/${id}/download`, {
        responseType: 'blob'
    }),

    /**
     * Get document placeholders
     * @param {number} id - Document ID
     * @returns {Promise} API response with extracted placeholders
     */
    getPlaceholders: (id) => Api().get(`/documents/${id}/placeholders`),

    /**
     * Replace document template file
     * @param {number} id - Document ID
     * @param {FormData} formData - Form data with file
     * @returns {Promise} API response
     */
    replaceFile: (id, formData) => Api().post(`/documents/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),

    /**
     * Rescan document for placeholders
     * @param {number} id - Document ID
     * @returns {Promise} API response with scan results
     */
    rescan: (id) => Api().post(`/documents/${id}/rescan`),

    /**
     * Get available modules
     * @returns {Promise} API response with module list
     */
    getModules: () => Api().get('/documents/modules'),
};

/**
 * Document Generation API
 */
export const generationAPI = {
    /**
     * Get generation history with optional filtering
     * @param {Object} params - Query parameters (document_id, customer_id, status, date_from, date_to, sort_field, sort_direction, per_page)
     * @returns {Promise} API response with generation history
     */
    getHistory: (params) => Api().get('/generations', { params }),

    /**
     * Get a single generation by ID
     * @param {number} id - Generation ID
     * @returns {Promise} API response with generation details
     */
    getById: (id) => Api().get(`/generations/${id}`),

    /**
     * Generate a single document
     * @param {Object} data - Generation data (document_id, customer_id, output_formats, password, placeholder_overrides)
     * @returns {Promise} API response with generated document
     */
    generateDocument: (data) => Api().post('/generate/document', data),

    /**
     * Generate multiple documents in batch
     * @param {Object} data - Batch generation data (document_ids, customer_id, output_format, password, merge_pdfs, merge_by_sequence)
     * @returns {Promise} API response with generated documents
     */
    generateBatch: (data) => Api().post('/generate/batch', data),

    /**
     * Download a previously generated document
     * @param {number} id - Generation ID
     * @param {string} format - 'docx' or 'pdf'
     * @returns {Promise} API response with file blob
     */
    download: (id, format = 'docx') => Api().get(`/generations/${id}/download`, {
        params: { format },
        responseType: 'blob'
    }),
};

/**
 * Third-Party Provider API
 */
export const thirdPartyProviderAPI = {
    /**
     * Get all third-party providers
     * @param {boolean} activeOnly - Filter only active providers
     * @returns {Promise} API response with providers list
     */
    getAll: (activeOnly = false) => Api().get('/third-party-providers', { 
        params: { active_only: activeOnly } 
    }),

    /**
     * Get a single provider by ID
     * @param {number} id - Provider ID
     * @returns {Promise} API response with provider details and documents
     */
    getById: (id) => Api().get(`/third-party-providers/${id}`),

    /**
     * Create a new provider (Super-Admin only)
     * @param {Object} data - Provider data (provider_name, provider_code, description, metadata, is_active)
     * @returns {Promise} API response with created provider
     */
    create: (data) => Api().post('/third-party-providers', data),

    /**
     * Update a provider (Super-Admin only)
     * @param {number} id - Provider ID
     * @param {Object} data - Updated provider data
     * @returns {Promise} API response
     */
    update: (id, data) => Api().put(`/third-party-providers/${id}`, data),

    /**
     * Delete a provider (Super-Admin only)
     * @param {number} id - Provider ID
     * @returns {Promise} API response
     */
    delete: (id) => Api().delete(`/third-party-providers/${id}`),

    /**
     * Get documents for a specific provider
     * @param {number} id - Provider ID
     * @returns {Promise} API response with provider's documents
     */
    getDocuments: (id) => Api().get(`/third-party-providers/${id}/documents`),
};

/**
 * Database Connection Utility API
 */
export const dbConnectionAPI = {
    /**
     * Get all database connections (Super-Admin only)
     * @returns {Promise} API response with connection list
     */
    getAll: () => Api().get('/database-connections'),

    /**
     * Get a single database connection by ID
     * @param {number} id - Connection ID
     * @returns {Promise} API response with connection details
     */
    getById: (id) => Api().get(`/database-connections/${id}`),

    /**
     * Create a new database connection (Super-Admin only)
     * @param {Object} data - Connection data (name, driver, host, port, database, username, password)
     * @returns {Promise} API response with created connection
     */
    create: (data) => Api().post('/database-connections', data),

    /**
     * Update a database connection (Super-Admin only)
     * @param {number} id - Connection ID
     * @param {Object} data - Updated connection data
     * @returns {Promise} API response
     */
    update: (id, data) => Api().put(`/database-connections/${id}`, data),

    /**
     * Delete a database connection (Super-Admin only)
     * @param {number} id - Connection ID
     * @returns {Promise} API response
     */
    delete: (id) => Api().delete(`/database-connections/${id}`),

    /**
     * Test database connection credentials
     * @param {Object} data - Connection credentials to test (driver, host, port, database, username, password)
     * @returns {Promise} API response with test result
     */
    test: (data) => Api().post('/database-connections/test', data),

    /**
     * Test a saved connection using stored credentials
     * @param {number} id - Connection ID
     * @returns {Promise} API response with test result
     */
    testSaved: (id) => Api().post(`/database-connections/${id}/test`),

    /**
     * Get tables from database connection
     * @param {number} connectionId - Database connection ID
     * @param {string} schema - Database schema (dgg or dbo), defaults to 'dbo'
     * @returns {Promise} API response with table list
     */
    getTables: (connectionId, schema = 'dbo') => Api().get(`/database-connections/${connectionId}/tables?schema=${schema}`),

    /**
     * Get fields from a table
     * @param {number} connectionId - Database connection ID
     * @param {string} tableName - Table name
     * @param {string} schema - Database schema (dgg or dbo), defaults to 'dbo'
     * @returns {Promise} API response with field list
     */
    getTableFields: (connectionId, tableName, schema = 'dbo') => Api().get(`/database-connections/${connectionId}/tables/${tableName}/fields?schema=${schema}`),

    /**
     * Get sample values for a specific field (preview)
     * @param {number} connectionId - Database connection ID
     * @param {string} tableName - Table name
     * @param {string} fieldName - Field name
     * @param {string} schema - Database schema (dgg or dbo), defaults to 'dbo'
     * @param {number} limit - Maximum number of samples, defaults to 5
     * @returns {Promise} API response with sample values
     */
    getFieldPreview: (connectionId, tableName, fieldName, schema = 'dbo', limit = 5) => Api().get(`/database-connections/${connectionId}/tables/${tableName}/fields/${fieldName}/preview?schema=${schema}&limit=${limit}`),
};

/**
 * LibreOffice Setup API
 * Methods for managing LibreOffice installation
 */
export const libreOfficeSetupAPI = {
    /**
     * Check LibreOffice installation status
     * @returns {Promise} API response with installation status
     */
    checkStatus: () => Api().get('/libreoffice-setup/status'),

    /**
     * Clean up LibreOffice installation and temp files
     * @returns {Promise} API response
     */
    cleanup: () => Api().post('/libreoffice-setup/cleanup'),

    /**
     * Run LibreOffice setup script (returns SSE stream)
     * @returns {Promise} API response
     */
    runSetup: () => Api().post('/libreoffice-setup/run'),

    /**
     * Commit and push LibreOffice installation to git (returns SSE stream)
     * @param {string} message - Commit message
     * @returns {Promise} API response
     */
    commitAndPush: (message) => Api().post('/libreoffice-setup/commit-push', { message }),
};

export default {
    placeholder: placeholderAPI,
    document: documentAPI,
    generation: generationAPI,
    dbConnection: dbConnectionAPI,
    libreOfficeSetup: libreOfficeSetupAPI,
};
